/**
 * i18nDb — charge les traductions gérées dans le superadmin (table `translations`)
 * directement dans i18next au runtime.
 *
 * Sans ce chargement, l'application n'a AUCUNE ressource de traduction bundlée
 * (les fichiers de langue sont gérés en base), donc changer de langue n'avait
 * aucun effet visible.
 */
import i18n from '@/i18n';
import { supabase } from '@/lib/supabase';

const loadedLangs = new Set<string>();
const pending: Record<string, Promise<void>> = {};

/**
 * Charge (une seule fois par langue) toutes les traductions d'une langue
 * depuis le Backend et les injecte dans i18next.
 */
export async function loadDbTranslations(code: string): Promise<void> {
  if (!code) return;
  if (loadedLangs.has(code)) return;
  if (pending[code]) return pending[code];

  pending[code] = (async () => {
    try {
      const { data, error } = await supabase
        .from('translations')
        .select('namespace, key, value')
        .eq('lang', code);
      if (error) return;
      const bundle: Record<string, string> = {};
      (data || []).forEach((row: { namespace: string; key: string; value: string }) => {
        if (row && row.namespace && row.key && row.value != null) {
          bundle[`${row.namespace}.${row.key}`] = row.value;
        }
      });
      if (Object.keys(bundle).length > 0) {
        i18n.addResourceBundle(code, 'translation', bundle, true, true);
      }
      loadedLangs.add(code);
    } catch {
      // Silencieux : on garde les libellés par défaut en cas d'échec réseau.
    } finally {
      delete pending[code];
    }
  })();

  return pending[code];
}

/** Invalide le cache après une modification dans le gestionnaire de traductions. */
export function invalidateDbTranslations(code?: string): void {
  if (code) loadedLangs.delete(code);
  else loadedLangs.clear();
}